import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SearchFilters from '../components/resources/SearchFilters';
import OrganizationCard from '../components/resources/OrganizationCard';
import type { LegalOrganization } from '../types';
import { API_BASE_URL } from '../config';

interface Filters {
  state: string;
  specialization: string;
  language: string;
  freeOnly: boolean;
}

const defaultFilters: Filters = {
  state: '',
  specialization: '',
  language: '',
  freeOnly: false,
};

export default function Resources() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [organizations, setOrganizations] = useState<LegalOrganization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchOrganizations() {
      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (filters.state) params.append('state', filters.state);
        if (filters.specialization) params.append('specialization', filters.specialization);
        if (filters.language) params.append('language', filters.language);
        if (filters.freeOnly) params.append('freeOnly', 'true');

        const response = await fetch(`${API_BASE_URL}/api/organizations?${params}`);
        const data = await response.json();

        if (data.success) {
          setOrganizations(data.data.organizations);
        } else {
          setError(data.error || 'Failed to fetch organizations');
        }
      } catch (err) {
        setError('Failed to connect to server');
      } finally {
        setIsLoading(false);
      }
    }

    fetchOrganizations();
  }, [filters]);

  const handleClearFilters = () => {
    setFilters(defaultFilters);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {t('resources.title')}
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          {t('resources.subtitle')}
        </p>
      </div>

      {/* Filters */}
      <div className="mb-8">
        <SearchFilters
          filters={filters}
          onChange={setFilters}
          onClear={handleClearFilters}
        />
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => setFilters({ ...filters })}
            className="btn-primary"
          >
            {t('common.retry')}
          </button>
        </div>
      ) : organizations.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-600 mb-4">{t('resources.noResults')}</p>
          <button
            onClick={handleClearFilters}
            className="btn-secondary"
          >
            {t('common.clearAll')}
          </button>
        </div>
      ) : (
        <>
          {/* Results count */}
          <p className="text-sm text-gray-500 mb-4">
            Showing {organizations.length} organization{organizations.length !== 1 ? 's' : ''}
          </p>

          {/* Organizations Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {organizations.map((org) => (
              <OrganizationCard key={org.id} organization={org} />
            ))}
          </div>
        </>
      )}

      {/* Help Section */}
      <div className="mt-16 bg-gray-50 rounded-2xl p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          Need Help Finding Resources?
        </h2>
        <p className="text-gray-600 mb-6">
          If you cannot find an organization in your area, here are some additional resources:
        </p>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-primary-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              <strong>Immigration Advocates Network:</strong>{' '}
              <a href="https://www.immigrationadvocates.org/legaldirectory/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                immigrationadvocates.org/legaldirectory
              </a>
            </span>
          </li>
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-primary-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              <strong>CLINIC (Catholic Legal Immigration Network):</strong>{' '}
              <a href="https://cliniclegal.org/affiliates" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                cliniclegal.org/affiliates
              </a>
            </span>
          </li>
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-primary-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              <strong>USCIS Office Locator:</strong>{' '}
              <a href="https://www.uscis.gov/about-us/find-uscis-office" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                uscis.gov/about-us/find-uscis-office
              </a>
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}
